import React, { useState, useEffect } from "react";
import { Link, useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import DashboardSideBar from "../components/DashboardSideBar";
import DashboardHeader from "../components/DashboardHeader";

const ManageUsers = () => {
  // Base API URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { authToken, user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/unauthorized");
      return;
    }

    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${
            API_BASE_URL ? API_BASE_URL : "http://localhost:8000/api"
          }/getusers`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setUsers(response.data);
      } catch (err) {
        setError("Failed to fetch users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [authToken, navigate, user, API_BASE_URL]);
  // Disable (soft-delete) user
  const handleDelete = async (id, email) => {
    if (!window.confirm("Are you sure you want to disable this user? " + email))
      return;

    try {
      await axios.delete(
        `${
          API_BASE_URL ? API_BASE_URL : "http://localhost:8000/api"
        }/users/delete/${id}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      alert("User disabled successfully");

      // Refresh user list
      const response = await axios.get(
        `${API_BASE_URL ? API_BASE_URL : "http://localhost:8000/api"}/getusers`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      setUsers(response.data);
    } catch (err) {
      alert("Failed to disable user");
    }
  };

  // Restore user
  const handleRestore = async (id, email) => {
    if (!window.confirm("Do you want to restore this user? " + email)) return;

    try {
      await axios.put(
        `${
          API_BASE_URL ? API_BASE_URL : "http://localhost:8000/api"
        }/users/restore/${id}`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      alert("User restored successfully");

      // Refresh user list
      const response = await axios.get(
        `${API_BASE_URL ? API_BASE_URL : "http://localhost:8000/api"}/getusers`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      setUsers(response.data);
    } catch (err) {
      alert("Failed to restore user");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardSideBar />
      <div className="md:ml-64 p-6">
        <DashboardHeader PageTitle="Manage Users" />

        <div className="max-w-7xl mx-auto">
          {/* Add User Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-green-100 mb-6">
            <h2 className="text-lg font-medium text-green-900 mb-4">
              Add New User
            </h2>
            <Link
              to="/register"
              className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                loading ? "bg-gray-400" : "bg-green-900 hover:bg-green-800"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400`}
            >
              Add User
            </Link>
          </div>

          {/* Users List */}
          <div className="bg-white rounded-lg shadow-sm border border-green-100">
            <div className="p-6">
              <h2 className="text-lg font-medium text-green-900 mb-4">
                Registered Users
              </h2>

              {loading ? (
                <p className="text-gray-600">Loading users...</p>
              ) : error ? (
                <p className="text-red-500">{error}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-green-900 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-green-900 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-green-900 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-green-900 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((user) => (
                        <tr
                          key={user.id}
                          className={
                            user.deleted_at ? "bg-gray-100 opacity-70" : ""
                          }
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.firstname} {user.lastname}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.role}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button className="text-yellow-600 hover:text-yellow-900 mr-4">
                              <NavLink
                                to={`/edit-user?id=${user.id}`}
                                className="text-yellow-600 hover:text-yellow-900 mr-4"
                              >
                                Edit
                              </NavLink>
                            </button>
                            {user.deleted_at ? (
                              <button
                                onClick={() =>
                                  handleRestore(user.id, user.email)
                                }
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Restore
                              </button>
                            ) : (
                              <button
                                onClick={() =>
                                  handleDelete(user.id, user.email)
                                }
                                className="text-red-600 hover:text-red-900"
                              >
                                Disable
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageUsers;
